import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Building2, 
  Check, 
  ArrowRight,
  Upload,
  Palette,
  User,
  Mail,
  Globe,
  CreditCard,
  Sparkles,
  Database,
  FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  provisionNewCompany, 
  checkSubdomainAvailability,
  generateCompanySlug 
} from '../../utils/firebase/companyProvisioning';

interface OnboardingStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

export default function TenantOnboarding({ onComplete }: { onComplete?: () => void }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    companyName: '',
    subdomain: '',
    contactEmail: '',
    contactName: '',
    phone: '',
    adminPassword: '',
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    logo: null as File | null,
    wellCount: '',
    userCount: '',
    emailCredentials: true,
  });
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdCompany, setCreatedCompany] = useState<any>(null);

  // Debug log to see component state

  const steps: OnboardingStep[] = [
    {
      id: 1,
      title: 'Company Information',
      description: 'Basic company details and contact',
      icon: <Building2 className="h-5 w-5" />,
    },
    {
      id: 2,
      title: 'Subdomain Setup',
      description: 'Choose your unique subdomain',
      icon: <Globe className="h-5 w-5" />,
    },
    {
      id: 3,
      title: 'Branding',
      description: 'Customize colors and logo',
      icon: <Palette className="h-5 w-5" />,
    },
    {
      id: 4,
      title: 'Plan Selection',
      description: 'Configure well and user limits',
      icon: <CreditCard className="h-5 w-5" />,
    },
  ];

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo must be less than 2MB');
        return;
      }
      setFormData({ ...formData, logo: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateSubdomain = (companyName: string) => {
    return companyName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleCompanyNameChange = (name: string) => {
    setFormData({
      ...formData,
      companyName: name,
      subdomain: formData.subdomain || generateSubdomain(name),
    });
  };

  const validateStep = () => {
    
    switch (currentStep) {
      case 1:
        if (!formData.companyName || !formData.contactName || !formData.contactEmail) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        break;
      case 2:
        if (!formData.subdomain) {
          toast.error('Please choose a subdomain');
          return false;
        }
        if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
          toast.error('Subdomain can only contain lowercase letters, numbers, and hyphens');
          return false;
        }
        break;
      case 4:
        if (!formData.wellCount || parseInt(formData.wellCount) < 1) {
          console.error('❌ Validation failed: wellCount is', formData.wellCount);
          toast.error('Please specify the number of wells');
          return false;
        }
        if (!formData.adminPassword || formData.adminPassword.length < 6) {
          console.error('❌ Validation failed: adminPassword length is', formData.adminPassword?.length);
          toast.error('Admin password must be at least 6 characters');
          return false;
        }
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      } else {
        handleComplete();
      }
    }
  };

  const handleComplete = async () => {
    setCreating(true);
    
    try {
      
      // Provision the new company with isolated storage and manager account
      const result = await provisionNewCompany({
        companyName: formData.companyName,
        subdomain: formData.subdomain,
        contactEmail: formData.contactEmail,
        contactName: formData.contactName,
        phone: formData.phone,
        adminPassword: formData.adminPassword,
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        wellCount: parseInt(formData.wellCount) || 0,
        userCount: parseInt(formData.userCount) || 5,
        logo: logoPreview,
      });
      
      
      // Reset creating state immediately after success
      setCreating(false);
      
      // Show success popup
      toast.success('Company Created Successfully!', {
        description: `${formData.companyName} has been created with isolated storage and manager account`,
        duration: 3000,
      });
      
      // Reset form
      setFormData({
        companyName: '',
        subdomain: '',
        contactEmail: '',
        contactName: '',
        phone: '',
        adminPassword: '',
        primaryColor: '#3B82F6',
        secondaryColor: '#10B981',
        logo: null,
        wellCount: '',
        userCount: '',
        emailCredentials: true,
      });
      setLogoPreview('');
      setCurrentStep(1);
      
      // Wait 2 seconds for user to see the success message, then navigate
      setTimeout(() => {
        if (onComplete) {
          onComplete();
        }
      }, 2000);
      
    } catch (error: any) {
      console.error('❌ Company provisioning failed:', error);
      toast.error('Failed to provision company', {
        description: error.message || 'Please try again',
      });
      setCreating(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                value={formData.companyName}
                onChange={(e) => handleCompanyNameChange(e.target.value)}
                placeholder="e.g., Acme Oil & Gas"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="contactName">Primary Contact Name *</Label>
              <Input
                id="contactName"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                placeholder="e.g., John Smith"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="contactEmail">Contact Email *</Label>
              <Input
                id="contactEmail"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                placeholder="e.g., admin@acmeoil.com"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number (Optional)</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="e.g., (555) 123-4567"
                className="mt-1"
              />
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="subdomain">Subdomain *</Label>
              <div className="mt-1 flex items-center">
                <Input
                  id="subdomain"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase() })}
                  placeholder="acme-oil"
                  className="rounded-r-none"
                />
                <div className="px-4 py-2 bg-gray-100 border border-l-0 rounded-r-lg text-gray-600">
                  .oilwellx.com
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                This will be the company's unique URL
              </p>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <Globe className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Preview</p>
                  <p className="text-sm text-blue-700 mt-1">
                    https://<span className="font-medium">{formData.subdomain || 'your-company'}</span>.oilwellx.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label>Company Logo (Optional)</Label>
              <div className="mt-2">
                {logoPreview ? (
                  <div className="flex items-center gap-4">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-24 h-24 object-contain rounded-lg border"
                    />
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFormData({ ...formData, logo: null });
                        setLogoPreview('');
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                    <div className="text-center">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">Click to upload logo</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 2MB</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleLogoUpload}
                    />
                  </label>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="primaryColor">Primary Color</Label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    id="primaryColor"
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    className="h-10 w-16 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="secondaryColor">Secondary Color</Label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    id="secondaryColor"
                    type="color"
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                    className="h-10 w-16 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            
            <div className="p-4 rounded-lg border" style={{ backgroundColor: formData.primaryColor + '10' }}>
              <p className="text-sm text-gray-600 mb-2">Preview</p>
              <div className="flex gap-2">
                <div
                  className="px-4 py-2 rounded text-white font-medium"
                  style={{ backgroundColor: formData.primaryColor }}
                >
                  Primary Button
                </div>
                <div
                  className="px-4 py-2 rounded text-white font-medium"
                  style={{ backgroundColor: formData.secondaryColor }}
                >
                  Secondary Button
                </div>
              </div>
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="wellCount">Number of Wells *</Label>
              <Input
                id="wellCount"
                type="number"
                min="1"
                value={formData.wellCount}
                onChange={(e) => setFormData({ ...formData, wellCount: e.target.value })}
                placeholder="e.g., 50"
                className="mt-1"
              />
              <p className="text-sm text-gray-500 mt-1">
                Billing: ${(parseInt(formData.wellCount) || 0) * 5}/month
              </p>
            </div>
            
            <div>
              <Label htmlFor="userCount">Maximum Users (Optional)</Label>
              <Input
                id="userCount"
                type="number"
                min="1"
                value={formData.userCount}
                onChange={(e) => setFormData({ ...formData, userCount: e.target.value })}
                placeholder="e.g., 10"
                className="mt-1"
              />
              <p className="text-sm text-gray-500 mt-1">
                Default: 5 users
              </p>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Admin Account Setup</h4>
              
              <div className="mb-3">
                <Label htmlFor="adminPassword">Admin Password *</Label>
                <Input
                  id="adminPassword"
                  type="password"
                  value={formData.adminPassword}
                  onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                  placeholder="Create secure password"
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Minimum 8 characters recommended
                </p>
              </div>

              {/* Email Credentials Checkbox */}
              <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="emailCredentials"
                  checked={formData.emailCredentials}
                  onChange={(e) => setFormData({ ...formData, emailCredentials: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="emailCredentials" className="flex-1 cursor-pointer">
                  <p className="text-sm font-medium text-gray-900">
                    Email login credentials to admin
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Send email with username, password, and login URL
                  </p>
                </label>
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-purple-600 mt-0.5" />
                <div>
                  <p className="font-medium text-purple-900">Summary</p>
                  <div className="mt-2 space-y-1 text-sm text-purple-700">
                    <p>Company: {formData.companyName}</p>
                    <p>Subdomain: {formData.subdomain}.oilwellx.com</p>
                    <p>Contact: {formData.contactEmail}</p>
                    <p>Monthly: ${(parseInt(formData.wellCount) || 0) * 5} ({formData.wellCount} wells)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-blue-600" />
            Company Onboarding Wizard
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Add a new company to the platform
          </p>
        </CardHeader>
        
        <CardContent>
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        step.id < currentStep
                          ? 'bg-green-600 text-white'
                          : step.id === currentStep
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {step.id < currentStep ? <Check className="h-5 w-5" /> : step.icon}
                    </div>
                    <p className={`text-xs mt-2 text-center ${
                      step.id === currentStep ? 'font-medium' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 ${
                        step.id < currentStep ? 'bg-green-600' : 'bg-gray-200'
                      }`}
                      style={{ maxWidth: '80px' }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            <h3 className="text-lg font-medium mb-4">{steps[currentStep - 1].title}</h3>
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-6 border-t">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1 || creating}
            >
              Back
            </Button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                Step {currentStep} of {steps.length}
              </span>
            </div>
            
            <Button
              onClick={() => {
                handleNext();
              }}
              disabled={creating}
              className="min-w-[120px]"
            >
              {creating ? (
                'Creating...'
              ) : currentStep === steps.length ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Complete
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}